import { buildExportMatrix, buildFooterRowsWithSource } from './exportClientView'
import { singleLine } from './exportCell'
import { formatLikeSample } from './reportGridUtils'

/**
 * Builds the report-data payload sent to cb-api's AI endpoints.
 *
 * Extracted from ReportAiSummary.jsx so the WIRE VALUES are directly testable.
 * The panel's own tests can only reach these through a render plus a mocked
 * service; the numbers below are the whole point of the module, so they get
 * asserted against live-captured values instead.
 *
 * ## Why numbers are formatted here (CC-53148)
 *
 * A live answer on Billable Hours (report 190) read *"Cole Martin, with
 * 4608.09688598 billable hours"*. The model was not hallucinating precision —
 * it was faithfully quoting what WE sent it.
 *
 * The grid's export view deliberately carries REAL numbers taken from CB's
 * `dataValue`, not the display string, so an .xlsx export stays summable rather
 * than landing as text. But CB's `dataValue` is unrounded while its
 * `displayValue` is not. Captured live from vladdemo:
 *
 * | column         | displayValue   | dataValue        |
 * | -------------- | -------------- | ---------------- |
 * | Billable Hours | `4,608.1`      | `4608.09688598`  |
 * | Billable $      | `1,865,787.20` | `1865787.2`      |
 * | Total (footer)  | `64,574.7`     | `64574.70214594` |
 *
 * So the AI was quoting a figure that appears NOWHERE in the product — 9 of the
 * 121 rows on that one report, which is why it read as intermittent.
 *
 * The fix rounds each numeric cell to its own column's display precision, which
 * both grids already publish as `decimals` (derived from a sample of CB's own
 * `displayValue` by `detectNumberFormat`, and used by the Excel/CSV/PDF writers
 * for exactly the same reason). `4608.09688598` → `"4608.1"` — the number on the
 * screen.
 *
 * Three things this deliberately does NOT do:
 *   - **It does not touch the export path.** `buildExportMatrix` still returns
 *     real unrounded numbers; the .xlsx and .csv are unchanged. The server
 *     export remains authoritative. Rounding is applied only to the copy of the
 *     data being handed to a language model.
 *   - **It does not add thousands separators or a currency symbol.** The model
 *     needs a parseable number, and `"4608.1"` is unambiguous where `"4,608.1"`
 *     invites a locale guess.
 *   - **It does not re-total anything.** Totals arrive already computed under the
 *     SUM-only guardrail (or verbatim from the server) and are only formatted.
 *
 * Rounding to the displayed precision can of course make two rows tie where the
 * raw values differ — but the on-screen grid has that same tie, and the AI
 * agreeing with the screen is the property worth having.
 *
 * Spec 153 holds that property to the digit for totals: CB's figures (cells, and
 * the totals CB computed) are quoted as CB displays them (`aiNumber`), and a total
 * the grid's footer RE-ADDS over the rows it shows is quoted exactly as that footer
 * prints it, through the footer's own formatter (`quoteShownReAdd`).
 */

// Money is the sensible default when a column publishes no precision of its own.
const FALLBACK_DECIMALS = 2

/**
 * `n` as text at `places` decimals, rounded half AWAY FROM ZERO on its DECIMAL
 * value — the way CB's own display rounds (spec 153).
 *
 * `toFixed` rounds the binary double, and a decimal midpoint is almost never exact
 * in binary: 508.45 is stored as 508.4499999999999886…, so `toFixed(1)` gave
 * "508.4" under CB's "508.5". A client re-add is worse still — report 190's eight
 * rows add up to 508.44999999999993. Measured live (190, 2026-01-01..06-30): that
 * total and three row cells (0.15, 104.85, 0.35) reached the AI one unit low.
 *
 * So the number is first read at 15 significant digits — the precision .NET
 * Framework (CB's runtime) formats a double from, which also absorbs the float
 * noise of a client re-add (508.44999999999993 → 508.450000000000) — and only then
 * rounded, as decimal digits, half away from zero, .NET's rule for a formatted
 * number (0.585 → "0.59", -0.585 → "-0.59"). A value whose last kept digit is
 * past 2^53 has no exact digits left to round, so it keeps `toFixed`.
 *
 * An integer below 2^53 skips the 15-digit read: it is exactly the value CB sent,
 * whatever type CB held it in, and has no fractional digit to round, so it is
 * written as it is. The read would only lose its 16th digit (1234567890123456
 * would come out as 1234567890123460).
 */
const roundHalfAwayFromZero = (n, places) => {
  if (Number.isSafeInteger(n)) {
    const text = places > 0 ? `${Math.abs(n)}.${'0'.repeat(places)}` : String(Math.abs(n))
    return n < 0 ? `-${text}` : text
  }
  const [mantissa, exponent = '0'] = Math.abs(n).toPrecision(15).split('e')
  const whole = Math.round(Number(`${mantissa}e${Number(exponent) + places}`))
  if (!Number.isSafeInteger(whole)) return n.toFixed(places)
  const digits = String(whole).padStart(places + 1, '0')
  const text = places > 0 ? `${digits.slice(0, -places)}.${digits.slice(-places)}` : digits
  return n < 0 && whole !== 0 ? `-${text}` : text
}

/**
 * Format one numeric cell for the AI payload at `decimals` places.
 *
 * With no usable `decimals` — a column that published none, or the specialized
 * path where there is no column metadata at all — this invents as little as it
 * can: an integer stays an integer (a count of `121` must not become `"121.00"`)
 * and only a fraction takes the money default. A nonsense precision is clamped to
 * 0–20 rather than throwing mid-payload.
 */
export const aiNumber = (n, decimals) => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return ''
  if (!Number.isFinite(decimals)) return Number.isInteger(n) ? String(n) : roundHalfAwayFromZero(n, FALLBACK_DECIMALS)
  return roundHalfAwayFromZero(n, Math.min(Math.max(Math.trunc(decimals), 0), 20))
}

/** Format a numeric cell with no column metadata to lean on. */
export const aiLooseNumber = (n) => aiNumber(n, undefined)

/**
 * One cell → its wire string, at its column's display precision. A multi-line
 * text cell (AP Detail's "Kayveo" over "82") is folded onto one line, "Kayveo 82"
 * (spec 133): cb-api joins a row's cells into ONE prompt line, so a raw "\n"
 * would split a row in two in front of the model.
 */
const cellForAi = (v, col) => {
  if (v == null) return ''
  if (typeof v === 'number') return aiNumber(v, col?.decimals)
  return singleLine(String(v))
}

// Spec 153: how a total's label names CB's figure for a declared non-SUM aggregate.
// A declared SUM is the bare column name, as it always was. The cb-api prompts quote
// these words (AGGREGATE_TOTAL_RULE, cb-api/src/domains/reports/routes.js), and a
// contract test in aiPayload.test.js holds the two files to the same words.
export const AGGREGATE_WORDS = Object.freeze({ AVG: 'average', MIN: 'minimum', MAX: 'maximum', COUNT: 'count' })

/**
 * Did a client-side filter leave rows out of the payload? Both grids publish
 * `visibleRows` as the filtered subset of `allRows`, so fewer of them means CB's
 * total covers rows the model is not shown. It is exactly the flat grid's own
 * `isFiltered`; on the grouped grid a filter that hides nothing sends exactly the
 * unfiltered payload, so it is labelled as the unfiltered one is.
 */
const rowsLeftOut = (view) =>
  Array.isArray(view?.visibleRows) && Array.isArray(view?.allRows) && view.visibleRows.length < view.allRows.length

/**
 * Does the grid's grand-total row SHOW a re-add of the rows on screen (spec 153)?
 *   - The grouped view says so itself, `totalsRecomputed`: a column filter or a row
 *     toggle is on (the grid's `isFiltered`, even one that hides nothing) and
 *     "Total just the filtered rows" is checked.
 *   - The flat view does not publish it. Its footer re-adds exactly when a filter
 *     left rows out (its own `isFiltered`), with that box at its default, checked.
 * Otherwise the footer shows CB's own figures.
 */
const footerShowsReAdd = (view) =>
  (typeof view?.totalsRecomputed === 'boolean' ? view.totalsRecomputed : rowsLeftOut(view))

/**
 * A re-added total, quoted as the grid's footer prints it (spec 153). Both grids
 * format a re-added footer figure with `formatLikeSample(<CB's footer display>)`
 * (ReportDataGrid's `footerValue`, ReportGroupedGrid's `footerCellContent`), and this
 * calls that same function on the same sample (`serverTotal` is CB's footer display),
 * dropping only the `$` and thousands separators (see the header).
 *
 * It is not `aiNumber`, because the two disagree on a float re-add that lands just
 * under a decimal midpoint. Measured on report 190 filtered to "c" (5 rows): Billable
 * Hours re-adds to 325.34999999999997, which the footer prints "325.3" and `aiNumber`
 * reads as "325.4". The AI quotes the "325.3" on the screen. (The decimal sum is
 * 325.35, so the screen's digit is the formatter's, and fixing `formatLikeSample`
 * fixes both, since this calls it.)
 *
 * The one difference from the screen: a re-add that is float noise below zero prints
 * "-0.00" there and "0.00" here. It is the same number.
 */
const quoteShownReAdd = (n, col) => {
  const sample = String(col?.serverTotal ?? '').trim()
  if (sample === '') return aiNumber(n, col?.decimals)
  const text = formatLikeSample(sample)(n).replace(/[$,]/g, '')
  return /^-0(\.0+)?$/.test(text) ? text.slice(1) : text
}

/**
 * A total's label (spec 153): the column name, qualified only when the figure is
 * not simply the sum of the rows sent —
 *   "Billable Fees (whole report, before your filter)"         CB's, rows left out
 *   "Actual Rate (average, whole report)"                       CB's declared AVG
 *   "Actual Rate (average, whole report, before your filter)"   both
 * The cb-api prompts carry one sentence telling the model what the qualifier
 * means; keep the words "whole report, before your filter" in step with it.
 */
export const totalLabel = (label, { aggregate, wholeReport, filtered }) => {
  const parts = []
  if (aggregate) parts.push(aggregate)
  if (wholeReport && (aggregate || filtered)) parts.push('whole report')
  if (wholeReport && filtered) parts.push('before your filter')
  return parts.length ? `${label} (${parts.join(', ')})` : label
}

/**
 * Build the AI payload from a universal-grid view getter (generic reports).
 *
 * Spec 133 — the totals are the export files' own totals row:
 * `buildFooterRows(cols, matrix, view)[0]`, WITH the view. It used to be
 * `buildTotalsRow(cols, matrix)` with no view, which skips the grouped grid's
 * "does re-adding the leaves agree with CB?" check (`reSumAgreesWithServer`), so on
 * a summary-grouped report the model was handed a client sum of leaves that are
 * not additive. Measured on the live run of 121 (Payments Received by Timekeeper
 * Summary): the AI
 * was told Amount 268,407.04 while CB's total, on screen and in every file export,
 * is 3,221,426.32. The prompt tells the model to lead with these totals.
 *
 * Spec 153 — each total now SAYS what it covers, from where the totals row got it
 * (`buildFooterRowsWithSource`):
 *   - A figure re-added over the rows sent keeps the bare column name: it is those
 *     rows' total, filtered or not.
 *   - CB's own figure, while a client filter has left rows out, is labelled
 *     "<column> (whole report, before your filter)". Measured live on report 50
 *     filtered to 3 timekeepers: the AI was told Billable Fees 2,883,748 — the
 *     firm — beside `rowCount: 3`, where the screen marks the same cell "keeps the
 *     server value for the full dataset". No filtered figure is computed in its
 *     place: those leaves are not additive, and the screen shows none either.
 *   - CB's figure for a column it declares AVG / MIN / MAX / COUNT (`aggregate`,
 *     published by the grouped view) is sent, labelled "<column> (average, whole
 *     report)". It used to be dropped (137's Actual Rate, 828.91 on screen). Only
 *     ever CB's figure: an average is never computed here.
 *   - A re-added total the footer shows re-added is quoted as the footer prints it
 *     (`quoteShownReAdd`); every other number as CB displays it (`aiNumber`).
 */
export const payloadFromView = (view, reportName) => {
  const { headers, matrix, cols } = buildExportMatrix(view, { honorColumns: true, honorFilters: true })
  if (!headers.length) throw new Error('There are no columns to summarize.')
  const { rows: footer, sources } = buildFooterRowsWithSource(cols, matrix, view)
  const totalsRow = footer[0] || []
  const filtered = rowsLeftOut(view)
  const reAddShown = footerShowsReAdd(view)
  const totals = cols
    .map((col, i) => {
      const aggregate = col.numeric ? AGGREGATE_WORDS[col.aggregate] : undefined
      const wholeReport = sources[i] === 'server'
      // A SUM-only column — or CB's own figure for a declared non-SUM aggregate,
      // never one this side re-added (that would be inventing an average).
      if (aggregate ? !wholeReport : !col.summable) return null
      const raw = totalsRow[i]
      // A recomputed sum is a raw float and carries the same noise as a cell
      // (the live footer above: 64574.70214594 under a screen reading 64,574.7).
      // Re-added while the footer shows the re-add: the footer's own digits. Any
      // other number is quoted the way CB displays a figure (`aiNumber`).
      // A server-total fallback is already CB's display string — leave it be.
      let value = raw
      if (typeof raw === 'number') {
        value = sources[i] === 'rows' && reAddShown ? quoteShownReAdd(raw, col) : aiNumber(raw, col.decimals)
      }
      return { label: totalLabel(col.label, { aggregate, wholeReport, filtered }), value }
    })
    .filter((t) => t && t.value !== '' && t.value != null)
  const rows = matrix.map((r) => r.map((v, i) => cellForAi(v, cols[i])))
  return { name: reportName, headers, totals, rows, rowCount: rows.length }
}

/**
 * Build the AI payload from a specialized report's { headers, rows } data (rows
 * are objects keyed by header, or arrays). Totals are left to the model — CB's
 * exports already include labeled total rows the model can read.
 *
 * Most of these pages are fed by CB's CSV, so their cells are already display
 * STRINGS and pass through untouched. The JSON-API pages (Balance Sheet, General
 * Ledger) can carry real numbers, and those get the loose format — there is no
 * per-column precision on this path to do better with.
 */
export const payloadFromData = (data, reportName) => {
  const headers = data.headers?.length ? data.headers : (data.rows?.[0] ? Object.keys(data.rows[0]) : [])
  if (!headers.length) throw new Error('There are no columns to summarize.')
  const cell = (v) => {
    if (v == null) return ''
    if (typeof v === 'number') return aiLooseNumber(v)
    // Spec 133: one prompt line per row here too (a CSV field can carry a raw "\n").
    return singleLine(String(v))
  }
  const rows = (data.rows || []).map((r) =>
    Array.isArray(r) ? r.map(cell) : headers.map((h) => cell(r[h]))
  )
  return { name: reportName, headers, totals: [], rows, rowCount: rows.length }
}

/** Build the report-data payload from whichever source this panel was given. */
export const buildPayload = ({ getView, data, reportName }) => {
  if (typeof getView === 'function') {
    const view = getView()
    if (!view) throw new Error('Run the report first, then ask about it.')
    return payloadFromView(view, reportName)
  }
  if (data && (data.rows?.length || data.headers?.length)) return payloadFromData(data, reportName)
  throw new Error('Run the report first, then ask about it.')
}
